/**
 * @class PersistenceManager
 * @description Manages Apollo cache persistence and timeout functionality.
 * Implements the Singleton pattern to ensure only one instance exists across the application.
 */
export class PersistenceManager {
  /**
   * Singleton instance of the PersistenceManager
   * @type {PersistenceManager|null}
   */
  static instance = null;

  /**
   * Timeout duration in milliseconds (defaults to 30 minutes)
   * @type {number}
   */
  static TIMEOUT = process.env.PERSISTENCE_TIMEOUT || 30 * 60 * 1000;

  /**
   * @constructor
   * @description Creates or returns the singleton instance of PersistenceManager
   */
  constructor() {
    if (!PersistenceManager.instance) {
      PersistenceManager.instance = this;

      /**
       * Flag indicating if the manager has been initialized
       * @type {boolean}
       */
      this.initialized = false;
    }
  }

  /**
   * @static
   * @method getInstance
   * @description Gets the singleton instance of PersistenceManager
   * @returns {PersistenceManager} The singleton instance
   */
  static getInstance() {
    if (!PersistenceManager.instance) {
      PersistenceManager.instance = new PersistenceManager();
    }

    return PersistenceManager.instance;
  }

  /**
   * @async
   * @method initialize
   * @description Initializes the persistence manager and checks for cache timeout
   * @param {Object} persistor - The Apollo cache persistor instance
   * @param {Function} persistor.purge - Function to clear the persisted cache
   * @param {string} persistKey - The key used for storing cache in session storage
   * @returns {Promise<Object>} Object containing wasPurged flag indicating if cache was cleared
   * @property {boolean} wasPurged - Indicates whether the cache was purged during initialization
   * @throws {Error} If there's an issue with session storage or cache purging
   */
  async initialize(persistor, persistKey) {
    if (this.initialized) return { wasPurged: false };

    const persistTimeoutKey = `${persistKey}-timeout`;
    const timestamp = window.sessionStorage.getItem(persistTimeoutKey);
    const now = Date.now();
    let wasPurged = false;

    if (!timestamp) {
      window.sessionStorage.setItem(persistTimeoutKey, now);
    } else {
      const timeDiff = now - parseInt(timestamp, 10);

      if (timeDiff > PersistenceManager.TIMEOUT) {
        await persistor.purge();
        wasPurged = true;
      }

      // Always update timestamp on access
      window.sessionStorage.setItem(persistTimeoutKey, now);
    }

    this.initialized = true;
    return { wasPurged };
  }
}

/**
 * The singleton instance of PersistenceManager
 * @type {PersistenceManager}
 * @constant
 */
export const persistenceManager = PersistenceManager.getInstance();
